import type { Book, BookCopy, Borrowing, Fine, ReadingProgram, ReadingProgress, Reservation } from "../../services/libraryService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The library's registers -- titles, copies, loans, reservations, fines and
 * reading programmes -- and the circulation rules the screens explain.
 */

/** Matches the server (apps/library/services.py): 14-day loans, two renewals, UGX 1,000 a day overdue. */
export const LOAN_DAYS = 14;
export const MAX_RENEWALS = 2;
export const DAILY_FINE = 1000;

export const money = (value: string | number | null | undefined) => `UGX ${Number(value ?? 0).toLocaleString()}`;
export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
const DAY_MS = 86_400_000;

export const CONDITIONS: [string, string][] = [
  ["excellent", "Excellent"],
  ["good", "Good"],
  ["fair", "Fair"],
  ["poor", "Poor"],
  ["damaged", "Damaged"],
];
export const PROGRAM_TYPES: [string, string][] = [
  ["challenge", "Reading challenge"],
  ["club", "Reading club"],
  ["competition", "Reading competition"],
  ["event", "Reading event"],
];
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

// --- Titles and copies -----------------------------------------------------------------

export const bookStatus = (book: Pick<Book, "available_copies" | "total_copies">) => {
  if (!book.total_copies) return { label: "No copies yet", dot: "bg-gray-400" };
  if (!book.available_copies) return { label: "All out", dot: "bg-amber-500" };
  return { label: `${book.available_copies} on the shelf`, dot: "bg-emerald-500" };
};

export const shelfPlace = (book: Pick<Book, "shelf_number" | "row_number" | "position">) =>
  [book.shelf_number && `Shelf ${book.shelf_number}`, book.row_number && `row ${book.row_number}`, book.position && `position ${book.position}`].filter(Boolean).join(", ");

export const bookConfig: RegisterConfig<Book> = {
  search: (row) => [row.title, row.subtitle, row.authors_display, row.isbn, row.category_name, row.tags, row.shelf_number].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "available", label: "On the shelf", match: (row) => row.available_copies > 0 },
    { key: "out", label: "All out", match: (row) => row.total_copies > 0 && row.available_copies === 0 },
    { key: "no-copies", label: "No copies", match: (row) => row.total_copies === 0 },
  ],
  category: { label: "Category", value: (row) => row.category_name || "Uncategorised" },
  sorts: [
    { key: "title", label: "Title (A–Z)", compare: byText<Book>((row) => row.title) },
    { key: "author", label: "Author (A–Z)", compare: byText<Book>((row) => `${row.authors_display} ${row.title}`) },
    { key: "-added", label: "Newest added", compare: reversed(byText<Book>((row) => row.created_at ?? "")) },
    { key: "-copies", label: "Most copies", compare: reversed(byNumber<Book>((row) => row.total_copies)) },
  ],
};

export const copyStatus = (copy: Pick<BookCopy, "status">) => {
  const dots: Record<string, string> = { available: "bg-emerald-500", borrowed: "bg-sky-500", reserved: "bg-amber-500", lost: "bg-red-500", damaged: "bg-red-500", maintenance: "bg-gray-400" };
  return { label: copy.status === "maintenance" ? "Being repaired" : humanise(copy.status), dot: dots[copy.status] ?? "bg-gray-400" };
};

export const copyConfig: RegisterConfig<BookCopy> = {
  search: (row) => [row.book_title, row.book_authors, row.copy_number, row.barcode, row.location].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "available", label: "Available", match: (row) => row.status === "available" },
    { key: "borrowed", label: "Lent out", match: (row) => row.status === "borrowed" },
    { key: "reserved", label: "Held", match: (row) => row.status === "reserved" },
    { key: "gone", label: "Lost or damaged", match: (row) => row.status === "lost" || row.status === "damaged" },
  ],
  category: { label: "Condition", value: (row) => labelFrom(CONDITIONS, row.condition) },
  sorts: [
    { key: "title", label: "Title (A–Z)", compare: byText<BookCopy>((row) => `${row.book_title} ${row.copy_number.padStart(6, "0")}`) },
    { key: "barcode", label: "Barcode", compare: byText<BookCopy>((row) => row.barcode ?? "") },
  ],
};

/** The next copy number for a title: "003" after "001" and "002". */
export function nextCopyNumber(copies: Pick<BookCopy, "copy_number">[]) {
  const numbers = copies.map((copy) => Number.parseInt(copy.copy_number, 10)).filter(Number.isFinite);
  const next = (numbers.length ? Math.max(...numbers) : 0) + 1;
  return String(next).padStart(3, "0");
}

// --- Loans ---------------------------------------------------------------------------------

/** Where a loan stands today: returned, lost, overdue by n days, due within the week, or out. */
export function loanStatus(loan: Pick<Borrowing, "status" | "due_date" | "is_overdue" | "days_overdue">, now = Date.now()) {
  if (loan.status === "returned") return { label: "Returned", dot: "bg-gray-400" };
  if (loan.status === "lost") return { label: "Lost", dot: "bg-red-500" };
  if (loan.status === "damaged") return { label: "Returned damaged", dot: "bg-red-500" };
  if (loan.is_overdue || Date.parse(loan.due_date) < now) {
    const days = loan.days_overdue || Math.max(1, Math.floor((now - Date.parse(loan.due_date)) / DAY_MS));
    return { label: `Overdue ${days} ${days === 1 ? "day" : "days"}`, dot: "bg-red-500" };
  }
  const left = Math.ceil((Date.parse(loan.due_date) - now) / DAY_MS);
  if (left <= 3) return { label: left <= 0 ? "Due today" : `Due in ${left} ${left === 1 ? "day" : "days"}`, dot: "bg-amber-500" };
  return { label: "Out", dot: "bg-sky-500" };
}

export const isOut = (loan: Pick<Borrowing, "status">) => loan.status === "active" || loan.status === "overdue";
export const isLate = (loan: Pick<Borrowing, "status" | "due_date" | "is_overdue">, now = Date.now()) => isOut(loan) && (loan.is_overdue || Date.parse(loan.due_date) < now);

/** The fine a late return would carry today, at the server's daily rate. */
export const overdueFineToday = (loan: Pick<Borrowing, "status" | "due_date" | "is_overdue" | "days_overdue">, now = Date.now()) =>
  isLate(loan, now) ? Math.max(1, loan.days_overdue || Math.floor((now - Date.parse(loan.due_date)) / DAY_MS)) * DAILY_FINE : 0;

/** Why a loan cannot be renewed, or "" when it can. */
export function renewalBlock(loan: Pick<Borrowing, "status" | "due_date" | "is_overdue" | "renewal_count">, reservedTitles: Set<string>, title: string, now = Date.now()) {
  if (!isOut(loan)) return "It is not out.";
  if (isLate(loan, now)) return "It is overdue: it must come back, not be renewed.";
  if (loan.renewal_count >= MAX_RENEWALS) return `It has been renewed ${MAX_RENEWALS} times already.`;
  if (reservedTitles.has(title)) return "Someone is waiting for this title.";
  return "";
}

export const loanConfig = (now = Date.now()): RegisterConfig<Borrowing> => ({
  search: (row) => [row.book_title, row.book_authors, row.borrower_name].join(" "),
  views: [
    { key: "out", label: "Out now", match: isOut },
    { key: "overdue", label: "Overdue", match: (row) => isLate(row, now) },
    { key: "due-soon", label: "Due this week", match: (row) => isOut(row) && !isLate(row, now) && Date.parse(row.due_date) - now <= 7 * DAY_MS },
    { key: "returned", label: "Returned", match: (row) => row.status === "returned" || row.status === "damaged" },
    { key: "lost", label: "Lost", match: (row) => row.status === "lost" },
    { key: "all", label: "All" },
  ],
  date: { label: "Borrowed", value: (row) => row.borrow_date },
  sorts: [
    { key: "due", label: "Due soonest", compare: byText<Borrowing>((row) => row.due_date) },
    { key: "-borrowed", label: "Newest loans", compare: reversed(byText<Borrowing>((row) => row.borrow_date)) },
    { key: "borrower", label: "Borrower (A–Z)", compare: byText<Borrowing>((row) => row.borrower_name) },
  ],
});

/** A default due date for the date picker: today plus the loan period, as YYYY-MM-DD. */
export const defaultDueDate = (now = new Date()) => new Date(now.getTime() + LOAN_DAYS * DAY_MS).toISOString().slice(0, 10);

// --- Reservations ------------------------------------------------------------------------

export const isOpenReservation = (row: Pick<Reservation, "status">) => row.status === "pending" || row.status === "active";

export const reservationStatus = (row: Pick<Reservation, "status">) => {
  const map: Record<string, { label: string; dot: string }> = {
    pending: { label: "Waiting", dot: "bg-amber-500" },
    active: { label: "Copy held", dot: "bg-sky-500" },
    fulfilled: { label: "Collected", dot: "bg-emerald-500" },
    cancelled: { label: "Cancelled", dot: "bg-gray-400" },
    expired: { label: "Expired", dot: "bg-gray-400" },
  };
  return map[row.status] ?? { label: humanise(row.status), dot: "bg-gray-400" };
};

export const reservationConfig: RegisterConfig<Reservation> = {
  search: (row) => [row.book_title, row.book_authors, row.student_name].join(" "),
  views: [
    { key: "open", label: "Open", match: isOpenReservation },
    { key: "fulfilled", label: "Collected", match: (row) => row.status === "fulfilled" },
    { key: "closed", label: "Cancelled or expired", match: (row) => row.status === "cancelled" || row.status === "expired" },
    { key: "all", label: "All" },
  ],
  date: { label: "Reserved", value: (row) => row.reservation_date },
  sorts: [
    { key: "queue", label: "Queue order", compare: (a, b) => a.book_title.localeCompare(b.book_title) || a.priority - b.priority },
    { key: "-reserved", label: "Newest first", compare: reversed(byText<Reservation>((row) => row.reservation_date)) },
    { key: "expiry", label: "Expiring soonest", compare: byText<Reservation>((row) => row.expiry_date) },
  ],
};

// --- Fines -------------------------------------------------------------------------------

export const FINE_TYPES: [string, string][] = [
  ["overdue", "Overdue"],
  ["damage", "Damage"],
  ["loss", "Lost book"],
  ["other", "Other"],
];

export const fineStatus = (row: Pick<Fine, "status">) => {
  const map: Record<string, { label: string; dot: string }> = {
    pending: { label: "Unpaid", dot: "bg-amber-500" },
    paid: { label: "Paid", dot: "bg-emerald-500" },
    waived: { label: "Waived", dot: "bg-sky-500" },
    cancelled: { label: "Cancelled", dot: "bg-gray-400" },
  };
  return map[row.status] ?? { label: humanise(row.status), dot: "bg-gray-400" };
};

export const unpaidTotal = (fines: Pick<Fine, "status" | "amount">[]) =>
  fines.filter((fine) => fine.status === "pending").reduce((sum, fine) => sum + Number(fine.amount), 0);

export const fineConfig: RegisterConfig<Fine> = {
  search: (row) => [row.student_name, row.book_title, row.reason, row.payment_reference, row.waiver_reason].join(" "),
  views: [
    { key: "pending", label: "Unpaid", match: (row) => row.status === "pending" },
    { key: "paid", label: "Paid", match: (row) => row.status === "paid" },
    { key: "waived", label: "Waived", match: (row) => row.status === "waived" },
    { key: "all", label: "All" },
  ],
  date: { label: "Issued", value: (row) => row.issued_date },
  category: { label: "Kind", value: (row) => labelFrom(FINE_TYPES, row.fine_type) },
  sorts: [
    { key: "-issued", label: "Newest first", compare: reversed(byText<Fine>((row) => row.issued_date)) },
    { key: "-amount", label: "Largest first", compare: reversed(byNumber<Fine>((row) => row.amount)) },
    { key: "student", label: "Pupil (A–Z)", compare: byText<Fine>((row) => row.student_name) },
  ],
};

// --- Reading programmes --------------------------------------------------------------------

/** Share of a target reached, 0-100, or null when the programme sets none. */
export function targetShare(progress: Pick<ReadingProgress, "books_read" | "pages_read">, program?: Pick<ReadingProgram, "target_books" | "target_pages">) {
  const shares = [
    program?.target_books ? progress.books_read / program.target_books : null,
    program?.target_pages ? progress.pages_read / program.target_pages : null,
  ].filter((value): value is number => value !== null);
  if (!shares.length) return null;
  return Math.min(100, Math.round(Math.min(...shares) * 100));
}

export const programStatus = (program: Pick<ReadingProgram, "start_date" | "end_date" | "is_active">, today = new Date().toISOString().slice(0, 10)) => {
  if (!program.is_active) return { label: "Closed", dot: "bg-gray-400" };
  if (today < program.start_date) return { label: "Starting soon", dot: "bg-sky-500" };
  if (today > program.end_date) return { label: "Ended", dot: "bg-gray-400" };
  return { label: "Running", dot: "bg-emerald-500" };
};

export const programConfig: RegisterConfig<ReadingProgram> = {
  search: (row) => [row.name, row.description, labelFrom(PROGRAM_TYPES, row.program_type)].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "running", label: "Running", match: (row) => programStatus(row).label === "Running" },
    { key: "soon", label: "Starting soon", match: (row) => programStatus(row).label === "Starting soon" },
    { key: "ended", label: "Ended", match: (row) => programStatus(row).label === "Ended" },
  ],
  category: { label: "Kind", value: (row) => labelFrom(PROGRAM_TYPES, row.program_type) },
  sorts: [
    { key: "-start", label: "Newest first", compare: reversed(byText<ReadingProgram>((row) => row.start_date)) },
    { key: "name", label: "Name (A–Z)", compare: byText<ReadingProgram>((row) => row.name) },
    { key: "-people", label: "Most readers", compare: reversed(byNumber<ReadingProgram>((row) => row.participant_count)) },
  ],
};
